import { Form, UnsafeFormik, useField } from "collections/Form";
import { DISMISS, useNotification } from "collections/Notifications";
import { FormikErrors } from "formik/dist/types";
import {
  GroupPolicy,
  GroupPolicyInput,
  PolicyType,
  useCreateGroupPolicyMutation,
  useUpdateGroupPolicyMutation,
  useUserGroupPoliciesPrioritiesQuery
} from "generated/graphql";
import { generatePath, Redirect, Route, Switch } from "polyrouter";
import { needs, useGuard } from "rbac";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DetailProps } from "routes/Manage/Inventory/View/DeviceDetails";
import "routes/Manage/Inventory/View/DeviceDetails.scss";
import { State } from "store";
import {
  desktopDeviceSettingChanged,
  resetDesktopDeviceSettingChanges,
  SettingChange
} from "store/desktopDeviceSettings";
import i18n, { useTranslation } from "utilities/i18n";
import { Layout } from "views/Main/Layouts";
import { DeviceHeader } from "../DeviceHeader";
import {
  DESKTOP_DEVICE_SETTINGS_NAV,
  ErrorDisplay
} from "../Settings-Shared/DeviceSettingsComponents";
import { DesktopDeviceFormSetting } from "./DesktopDeviceSettings";
import { useGetDesktopDeviceSettingData } from "./hooks";
import MyDesktopDeviceSettings, {
  getUnsavedChangesCount
} from "./MyDesktopDeviceSettings";
import {
  CapabilitiesMap,
  constructGroupPolicyName,
  DesktopDevicePolicyScope,
  DevicePolicyInfo,
  HardwareModelProps,
  setDesktopDevicePolicyScope,
  useDesktopDevicePolicyScopeContext,
  UserGroupInfo
} from "./ProvisionedDesktopDeviceSettingsRouter";
import { BasicDesktopDeviceSettings } from "./schemas/types";
import {
  AdvancedSettings,
  GeneralSettings,
  RingtonesVolumeSettings,
  AudioSettings,
  SensorsPresenceSettings,
  SoftphonesMediaPlayersSettings,
  VideoControlsSettings,
  DiagnosticsSettings,
  WirelessSettings,
  SoftwareUpdateSettings
} from "./Shared/DesktopDeviceCategories";
import { DesktopDeviceSetting } from "./Shared/DesktopDeviceSetting";
import {
  createC10QueryObj,
  generateC10String,
  getC10Value
} from "./utilities/c10";
import { getPriority, getScopeRule } from "./utilities/utils";
import {
  GroupInfo,
  SiteInfo
} from "../Settings/ProvisionedDeviceSettingsRouter";
import { CombinedError } from "@urql/core";
import { OperationContext } from "urql";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export enum PolicyTypes {
  MODEL = "model",
  GROUP = "group",
  SITE = "site",
  USER_GROUP = "user_group",
  DEVICE = "device"
}

const GENERAL = "GENERAL";

export const ProvisionedCategorySetting = ({
  accessor,
  options,
  tooltipText,
  type,
  revert,
  index,
  WhenHidden
}: DesktopDeviceFormSetting) => {
  const userCanEdit = useGuard(needs("update:device"));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    settings,
    disableDependency,
    hideDependency,
    dropdownOptions,
    ...rest
  } = useGetDesktopDeviceSettingData(accessor, options, type);

  const [state] = useDesktopDevicePolicyScopeContext();
  const showInheritance =
    state.desktopDevicePolicyScope === DesktopDevicePolicyScope.Device;

  const disabled = !userCanEdit || disableDependency;
  const changed = useSelector(
    (state: State) => state.desktopDeviceSettings[accessor]
  );
  const inheritance = useSelector(
    (state: State) =>
      state.desktopDeviceSettingInheritance[generateC10String(accessor)]
  );

  const [, { initialValue, value }] = useField(settings.name);

  useEffect(() => {
    if (changed !== SettingChange.REVERTED) {
      dispatch(
        desktopDeviceSettingChanged({
          accessor,
          change: value !== initialValue ? SettingChange.EDITED : ""
        })
      );
    }
  }, [value, initialValue, dispatch, settings.name, accessor, changed]);

  return (
    <>
      {index === 0 && showInheritance && (
        <div className="legend grid">
          <div className="col-4 offset-8">{t("POLICY_INHERITANCE")}</div>
        </div>
      )}

      {hideDependency && !!WhenHidden && (
        <WhenHidden options={dropdownOptions} />
      )}
      <DesktopDeviceSetting
        className="provisioned-setting"
        key={accessor}
        accessor={accessor}
        inheritance={inheritance}
        type={type}
        settings={settings}
        changed={changed}
        tooltipText={tooltipText}
        disabled={disabled}
        revert={revert}
        fromPolicy={true}
        hideDependency={hideDependency}
        dropdownOptions={dropdownOptions}
        {...rest}
      />
    </>
  );
};

export const getGroupPolicyTitle = (
  type?: string,
  entityName?: string
): string => {
  let policyTitle = "";
  if (type === PolicyTypes.MODEL || type === DesktopDevicePolicyScope.Model) {
    policyTitle = `| ${i18n.t("DEVICE_MODEL_POLICY")}`;
  } else if (
    type === PolicyTypes.SITE ||
    type === DesktopDevicePolicyScope.Site
  ) {
    policyTitle = `| ${i18n.t("SITE_POLICY")} | ${entityName}`;
  } else if (
    type === PolicyTypes.GROUP ||
    type === DesktopDevicePolicyScope.Group
  ) {
    policyTitle = `| ${i18n.t("GROUP_POLICY")} | ${entityName}`;
  } else if (
    type === PolicyTypes.USER_GROUP ||
    type === DesktopDevicePolicyScope.User_Group
  ) {
    policyTitle = `| ${i18n.t("USER_GROUP_POLICY")} | ${entityName}`;
  } else if (
    type === PolicyTypes.DEVICE ||
    type === DesktopDevicePolicyScope.Device
  ) {
    policyTitle = `${i18n.t("POLICY")}`;
  }

  return policyTitle;
};

export const ProvisionedDesktopDeviceSettings: React.FC<{
  device?: DetailProps;
  group?: GroupInfo;
  site?: SiteInfo;
  userGroup?: UserGroupInfo;
  hardwareModel?: HardwareModelProps;
  policyScope: DesktopDevicePolicyScope;
  settingsPolicy?: GroupPolicy;
  deviceSettings: BasicDesktopDeviceSettings;
  isPolicyView?: boolean;
  categoryWithSettings: string[];
  capabilitiesMap: CapabilitiesMap;
  refreshData: (opts?: Partial<OperationContext> | undefined) => void;
  devicePolicyInfo?: DevicePolicyInfo;
}> = ({
  device,
  hardwareModel,
  site,
  group,
  userGroup,
  settingsPolicy,
  isPolicyView,
  deviceSettings,
  categoryWithSettings,
  capabilitiesMap,
  policyScope,
  refreshData,
  devicePolicyInfo
}) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  // Redux unsaved settings
  const dispatch = useDispatch();
  const unsavedSettings = useSelector(
    (state: State) => state.desktopDeviceSettings
  );
  const [, dispatchContext] = useDesktopDevicePolicyScopeContext();
  const [, updateGroupPolicy] = useUpdateGroupPolicyMutation();
  const [, createGroupPolicy] = useCreateGroupPolicyMutation();
  const tenantId = useTenantId();

  const { data: userGroupPolicies } = useUserGroupPoliciesPrioritiesQuery({
    variables: {
      rules: {
        and: [
          { equal: { key: "tenantId", value: tenantId } },
          { equal: { key: "user_group", value: userGroup?.id } }
        ]
      }
    },
    pause: !userGroup?.id
  });

  useEffect(() => {
    dispatchContext(setDesktopDevicePolicyScope(policyScope));
  }, [dispatchContext, policyScope]);

  const validate = (values: BasicDesktopDeviceSettings) => {
    let errors: FormikErrors<BasicDesktopDeviceSettings> = {};
    return errors;
  };
  const [redirect, setRedirect] = useState<boolean>(false);

  const submit = async (values: BasicDesktopDeviceSettings) => {
    const capabilitiesToUpdate: CapabilitiesMap = [];
    const capabilitiesToDelete: string[] = [];
    Object.entries(unsavedSettings).forEach(([accessor, change]) => {
      if (change === SettingChange.REVERTED) {
        capabilitiesToDelete.push(generateC10String(accessor, false));
      } else {
        const value = getC10Value(
          values,
          generateC10String(accessor).split(".")
        );
        capabilitiesToUpdate.push({
          id: generateC10String(accessor, false),
          value
        });
      }
    });

    const capabilitiesObject = createC10QueryObj(capabilitiesToUpdate);

    const scopeRule = getScopeRule(
      policyScope,
      {
        site: site?.id,
        group: group?.id,
        user_group: userGroup?.id,
        device: devicePolicyInfo?.id
      },
      hardwareModel?.id
    );
    const priority = getPriority(
      policyScope,
      settingsPolicy?.priority ??
        userGroupPolicies?.policiesCapabilities[0]?.priority
    );

    let errorResponse: CombinedError | undefined;

    if (settingsPolicy?.id) {
      const { error } = await updateGroupPolicy({
        input: {
          id: settingsPolicy?.id,
          capabilities: capabilitiesObject,
          pathsToRemove: capabilitiesToDelete
        }
      });
      errorResponse = error;
    } else {
      const policyInput: GroupPolicyInput = {
        name: constructGroupPolicyName(
          hardwareModel?.name || "",
          PolicyType.DeviceSwUpdate,
          policyScope,
          site?.name,
          group?.name,
          userGroup?.name,
          devicePolicyInfo?.id
        ),
        priority,
        rule: {
          and: [
            { equal: { key: "tenantId", value: tenantId } },
            { equal: { key: "modelId", value: hardwareModel?.id } },
            ...scopeRule
          ]
        },
        capabilities: capabilitiesObject,
        pathsToRemove: capabilitiesToDelete
      };
      const { error } = await createGroupPolicy({ input: policyInput });
      errorResponse = error;
    }

    const policyTypeStrings = {
      MODEL: t("MODEL_POLICY"),
      GROUP: t("GROUP_POLICY"),
      SITE: t("SITE_POLICY"),
      USER_GROUP: t("USER_GROUP_POLICY"),
      DEVICE: t("DEVICE"),
      unknown: ""
    };

    if (!errorResponse) {
      if (!settingsPolicy?.id) {
        refreshData({ requestPolicy: "network-only" });
        addNotification({
          id: "save-policy-success",
          type: "success",
          message: t("POLICY_CREATED"),
          dismiss: DISMISS.DEFAULT
        });
      } else {
        addNotification({
          id: "save-policy-success",
          type: "success",
          message: t("POLICY_SAVED", {
            policy: policyTypeStrings[policyScope || "unknown"]
          }),
          dismiss: DISMISS.DEFAULT
        });
      }
    }
  };

  const { Content } = Layout;
  const emptyPolicy = !settingsPolicy?.id;
  const entityName =
    userGroup?.name ||
    site?.name ||
    group?.name ||
    devicePolicyInfo?.name ||
    undefined;
  const policyTitle = getGroupPolicyTitle(policyScope, entityName);
  const pageTitle = isPolicyView ? (
    <>
      {policyScope === DesktopDevicePolicyScope.Device
        ? entityName
        : hardwareModel?.name}
      <p className="inline-block device-header-name">{policyTitle}</p>
    </>
  ) : (
    <>
      {device?.hardwareModel}{" "}
      <p className="inline-block device-header-name">{` | ${device?.name ||
        device?.room?.name ||
        device?.macAddress}`}</p>
    </>
  );

  const unsavedChanges = getUnsavedChangesCount(unsavedSettings);

  useEffect(() => {
    if (Object.keys(deviceSettings).length < 1) {
      if (redirect) {
        addNotification({
          type: "info",
          message: t("POLICY_UNAVAILABLE", { device: hardwareModel?.name }),
          dismiss: DISMISS.DEFAULT,
          persistThroughNav: true
        });
      }
      setRedirect(true);
    }

    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceSettings, redirect, hardwareModel?.name]);

  if (redirect) {
    return <Redirect to={`/manage/policy/list/${settingsPolicy?.type}`} />;
  }

  return (
    <UnsafeFormik
      name="DeviceSettings"
      initialValues={deviceSettings}
      customDimensions={{
        model: device?.hardwareModel,
        software_version: device?.softwareVersion
      }}
      enableReinitialize={true}
      validate={validate}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={submit}
    >
      {({ dirty }) => {
        return (
          <Form className="form pb-2">
            <DeviceHeader
              device={device}
              pageTitle={pageTitle}
              hasActionButtons
              unsavedChanges={unsavedChanges}
              onDiscard={() => dispatch(resetDesktopDeviceSettingChanges())}
            >
              {emptyPolicy && (
                <div className="unchanged-policy-note">
                  {t("CHANGE_POLICY_TO_SAVE_NOTE")}
                </div>
              )}
              <ErrorDisplay />
            </DeviceHeader>

            <Switch>
              <Route nest path="my-settings">
                <MyDesktopDeviceSettings
                  deviceSettings={deviceSettings}
                  capabilitiesMap={capabilitiesMap}
                  device={devicePolicyInfo}
                  deviceModel={hardwareModel?.name || ""}
                  site={site?.name}
                  group={group?.name}
                  userGroup={userGroup?.name}
                  dirty={dirty || unsavedChanges > 0}
                />
              </Route>
              <Content.ScrollingList>
                <Route
                  nest
                  path="general"
                  render={() => (
                    <GeneralSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="ringtones-volume"
                  render={() => (
                    <RingtonesVolumeSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="audio"
                  render={() => (
                    <AudioSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="sensors-presence"
                  render={() => (
                    <SensorsPresenceSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="softphones-media-players"
                  render={() => (
                    <SoftphonesMediaPlayersSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="wireless"
                  render={() => (
                    <WirelessSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="advanced"
                  render={() => (
                    <AdvancedSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="video-controls"
                  render={() => (
                    <VideoControlsSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="diagnostics"
                  render={() => (
                    <DiagnosticsSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route
                  nest
                  path="software-update"
                  render={() => (
                    <SoftwareUpdateSettings
                      CategorySetting={ProvisionedCategorySetting}
                    />
                  )}
                />
                <Route nest path=":category?">
                  {({ match }) => {
                    // this is used to match routes like "/",
                    // and redirect to the full path "/category"
                    let { category } = match?.params;
                    let path = "";
                    if (!category) {
                      const defaultCategory =
                        categoryWithSettings.length > 0
                          ? categoryWithSettings[0]
                          : GENERAL;
                      category = DESKTOP_DEVICE_SETTINGS_NAV[
                        defaultCategory
                      ]?.url.replace(/^\//, "");
                      path += "/:category";
                    }

                    if (!category) {
                      return;
                    }

                    return (
                      <>
                        <Redirect
                          nest
                          persist={[
                            "PolicyFilterState",
                            "deviceModel",
                            "site",
                            "user_group",
                            "device",
                            "DeviceFilterState",
                            "InsightsFilterState",
                            "group",
                            "previous"
                          ]}
                          to={generatePath(path, {
                            category
                          })}
                        />
                      </>
                    );
                  }}
                </Route>
              </Content.ScrollingList>
            </Switch>
          </Form>
        );
      }}
    </UnsafeFormik>
  );
};
